import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowUpRight,
    ArrowRight,
    ShieldCheck,
    Scale,
    Smartphone,
    CalendarCheck,
    ReceiptText,
    PieChart,
    Quote,
} from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import Simulator from "@/components/Simulator";
import SwitchAccountant from "@/components/SwitchAccountant";
import { MaskedLine, FadeIn, Eyebrow } from "@/components/Reveal";
import { Sparkle } from "@/components/Logo";
import IloveMeIcon from "@/components/IloveMeIcon";
import { ILOVEME_URL } from "@/lib/site";

const HERO_IMG =
    "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxiZWF1dHklMjBzYWxvbiUyMGVudHJlcHJlbmV1cnxlbnwwfHx8fDE3ODg2MTE0OTN8MA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
    {
        number: "01",
        icon: Scale,
        tag: "Segurança jurídica",
        title: "Apoio especializado na Lei do Salão Parceiro",
        text: "Orientação completa para a adequação à Lei do Salão Parceiro (Lei nº 13.352/2016): contratos de parceria, divisão de cotas e rotinas estruturadas conforme os requisitos aplicáveis. O cumprimento da lei também depende das práticas do estabelecimento e dos profissionais, e a gente orienta você em cada etapa.",
    },
    {
        number: "02",
        icon: ShieldCheck,
        tag: "Inteligência tributária",
        title: "Tributos corretos para o seu modelo de negócio",
        text: "Enquadramento fiscal revisado e orientação sobre notas discriminadas entre cota-salão e cota-parceiro, para que a tributação acompanhe a realidade da sua operação, com transparência perante o Fisco.",
    },
    {
        number: "03",
        icon: Smartphone,
        tag: "Tecnologia integrada",
        title: "Uma rotina mais organizada com o ilove.me",
        text: "Agenda, clientes, comissões, caixa e estoque organizados na plataforma ilove.me, com a Contabeleza ao lado para uma visão integrada da operação. Mais tempo para o que você faz de melhor.",
    },
];

const PROFILES = [
    {
        title: "Salões e Barbearias",
        text: "Parceiros regularizados, comissões organizadas e uma rotina de caixa mais leve.",
        img: "https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxiZWF1dHklMjBzYWxvbiUyMGVudHJlcHJlbmV1cnxlbnwwfHx8fDE3ODg2MTE0OTN8MA&ixlib=rb-4.1.0&q=85",
        to: "/salao-de-beleza",
        testId: "tab-audience-salons",
    },
    {
        title: "Clínicas de Estética",
        text: "Tributação sob medida para serviços de estética e uma operação fiscal organizada.",
        img: "https://images.pexels.com/photos/16571735/pexels-photo-16571735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        to: "/para-quem",
        testId: "tab-audience-clinics",
    },
    {
        title: "Profissionais da Beleza",
        text: "Saia da informalidade com CNPJ, nota fiscal e contrato de parceria adequado à lei.",
        img: "https://images.unsplash.com/photo-1695527081848-1e46c06e6458?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBoYWlyJTIwc2Fsb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODg2MTE0OTN8MA&ixlib=rb-4.1.0&q=85",
        to: "/para-quem",
        testId: "tab-audience-professionals",
    },
];

const TESTIMONIALS = [
    {
        quote: "Nem acredito que passamos anos pagando imposto que não precisava.",
        name: "Luciana e Conrado",
        business: "Beauty Studio, Rio de Janeiro",
        img: "https://framerusercontent.com/images/hgql4mnkm3yCBBJyFbmcWA8Kqg.jpg?width=284&height=284",
    },
    {
        quote: "Vou passar a dormir tranquila sem medo de ações trabalhistas.",
        name: "Rosi",
        business: "Salão de Beleza, Taboão da Serra",
        img: "https://framerusercontent.com/images/Nl92rPiWNbcB5Qwt6adv4cKSzE.jpg?width=284&height=284",
    },
    {
        quote: "Se não fosse a Contabeleza não tinha resistido à pandemia.",
        name: "Will",
        business: "Salão de Beleza e Barbearia, São Paulo",
        img: "https://framerusercontent.com/images/1s5S1619YmnRX5BR9Tz5Js57Sw.jpg?width=284&height=284",
    },
    {
        quote: "Todo o negócio de estética deveria ter um parceiro assim.",
        name: "Eva",
        business: "Clínica de Estética, Jundiaí",
        img: "https://framerusercontent.com/images/gqdRhyp9eQ3pdBJp97YTfcIH8.jpg?width=284&height=284",
    },
    {
        quote: "A Contabeleza oferece serviços completos e com extrema qualidade, além de solucionar com agilidade.",
        name: "Equipe Jacques Janine",
        business: "Rede de salões, São Paulo",
        img: null,
    },
    {
        quote: "Uma falha na contabilidade ou no financeiro pode ser fatal para a empresa, e a Contabeleza nos passa muita confiança.",
        name: "Equipe Werner",
        business: "Rede de salões, Rio de Janeiro",
        img: null,
    },
];

const ILOVE_POINTS = [
    { icon: CalendarCheck, text: "Agenda online e gestão de clientes" },
    { icon: PieChart, text: "Apoio no acompanhamento de comissões e cotas" },
    { icon: ReceiptText, text: "Orientação para notas fiscais discriminadas" },
    { icon: Smartphone, text: "Caixa, estoque e relatórios em um só painel" },
];

export default function HomePage() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <>
            <Seo
                title="Contabeleza, Contabilidade especializada para o mercado da beleza"
                description="Contabilidade especializada em salões de beleza, clínicas de estética e profissionais da beleza. Especialistas na Lei do Salão Parceiro, em ecossistema integrado ao ilove.me."
            />

            <section
                ref={heroRef}
                data-testid="hero-section-home"
                className="grain relative overflow-hidden bg-[#060B3A] pb-24 pt-32 sm:pt-40"
            >
                <div className="pointer-events-none absolute inset-0 hero-grid-lines opacity-60" />
                <motion.div style={{ y: glowY }} className="pointer-events-none absolute inset-0">
                    <div className="aurora-blob -left-32 -top-32 h-[520px] w-[520px] bg-[#0047BB]/60" />
                    <div
                        className="aurora-blob right-[-8rem] top-20 h-[440px] w-[440px] bg-[#8B3EE0]/40"
                        style={{ animationDelay: "-5s" }}
                    />
                    <div
                        className="aurora-blob bottom-[-10rem] left-1/3 h-[420px] w-[420px] bg-[#F35E6A]/30"
                        style={{ animationDelay: "-9s" }}
                    />
                </motion.div>

                <div className="relative mx-auto grid max-w-7xl items-center gap-20 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
                    <div>
                        <h1
                            data-testid="hero-heading-manifesto"
                            className="font-display text-[2.9rem] font-semibold leading-[1.04] tracking-tight text-white sm:text-7xl lg:text-[5.4rem]"
                        >
                            <MaskedLine delay={0.15}>Sua gestão</MaskedLine>
                            <MaskedLine delay={0.3}>com quem domina</MaskedLine>
                            <MaskedLine delay={0.45}>
                                <span className="text-shimmer">a lei da beleza.</span>
                            </MaskedLine>
                        </h1>

                        <FadeIn delay={0.65} className="mt-8 max-w-xl">
                            <p className="border-l-2 border-[#FCB21E] pl-5 text-lg leading-8 text-[#E4EEFF] sm:text-xl sm:leading-9">
                                Contabilidade, gestão e tecnologia em um único ecossistema para salões,
                                barbearias, clínicas de estética e profissionais da beleza.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.8} className="mt-10 flex flex-col gap-4 sm:flex-row">
                            <Link
                                to="/contato"
                                data-testid="btn-hero-primary-diagnostic"
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#0047BB] px-8 py-4 text-base font-semibold text-white shadow-[0_16px_40px_rgba(0,71,187,0.55)] ring-1 ring-white/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#0A55D4] active:scale-[0.97]"
                            >
                                <span>
                                    Solicitar diagnóstico{" "}
                                    <span className="text-shimmer text-shimmer-cta font-bold uppercase tracking-wide">
                                        gratuito
                                    </span>
                                </span>
                                <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                            <a
                                href={ILOVEME_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="btn-hero-secondary-ecosystem"
                                className="glass inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-white/20 active:scale-[0.97]"
                            >
                                Conhecer a <IloveMeIcon className="inline-flex items-center gap-1" />
                            </a>
                        </FadeIn>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="relative mx-auto w-full max-w-[15rem] sm:max-w-xs lg:max-w-sm"
                    >
                        <div className="orbit-ring pointer-events-none absolute -inset-8 rounded-full border border-dashed border-[#B2D0FF]/25" />
                        <motion.div style={{ y: imgY }} className="relative">
                            <div className="gota-frame relative aspect-[4/5] border-2 border-white/25 shadow-[0_40px_100px_rgba(139,62,224,0.35)]">
                                <img
                                    src={HERO_IMG}
                                    alt="Interior de salão de beleza contemporâneo atendido pela Contabeleza"
                                    className="h-full w-full object-cover"
                                    loading="eager"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#060B3A]/60 via-transparent to-[#8B3EE0]/10" />
                            </div>

                            <div className="pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[125%] -translate-x-1/2 -translate-y-1/2">
                                <div className="orbit-track relative h-full w-full">
                                    <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                                        <div className="orbit-card glass w-max rounded-2xl p-3 shadow-xl sm:p-4">
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FCB21E]">
                                                Lei nº 13.352/2016
                                            </p>
                                            <p className="mt-1 font-display text-base text-white sm:text-lg">
                                                Especialistas na Lei Salão Parceiro
                                            </p>
                                            <p className="mt-0.5 text-xs text-white/70">na prática, todos os dias</p>
                                        </div>
                                    </div>
                                    <div className="absolute left-[93%] top-[75%] -translate-x-1/2 -translate-y-1/2">
                                        <div className="orbit-card glass flex w-max items-center gap-2.5 rounded-2xl px-3 py-2.5 shadow-xl sm:px-4 sm:py-3">
                                            <CalendarCheck className="h-5 w-5 shrink-0 text-[#FCB21E]" />
                                            <span className="font-display text-base leading-snug text-white sm:text-lg">
                                                Agenda + gestão + reservas
                                                <br />
                                                <span className="font-sans text-xs text-white/70">com ilove.me</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="absolute left-[7%] top-[75%] -translate-x-1/2 -translate-y-1/2">
                                        <div className="orbit-card glass flex w-max items-center gap-2.5 rounded-2xl px-3 py-2.5 shadow-xl sm:px-4 sm:py-3">
                                            <Sparkle className="h-5 w-5 shrink-0" />
                                            <span className="font-display text-base leading-snug text-white sm:text-lg">
                                                100% dedicados
                                                <br />
                                                ao mercado da beleza
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>


            <section data-testid="manifesto-chapters-container" className="px-5 py-20 sm:px-8 sm:py-28">
                <div className="mx-auto max-w-7xl">
                    <FadeIn>
                        <Eyebrow>O manifesto Contabeleza</Eyebrow>
                        <h2 className="mt-4 max-w-5xl font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl lg:text-5xl">
                            Três capítulos. Um único compromisso: o seu negócio de beleza mais seguro e em crescimento.
                        </h2>
                        <p className="mt-5 text-lg font-medium text-[#0047BB] sm:text-xl">
                            Sem contabilês, sem burocracia, sem sustos.
                        </p>
                    </FadeIn>

                    <div className="mt-14 grid gap-6 lg:grid-cols-2">
                        {CHAPTERS.map((chapter, i) => {
                            const featured = i === 0;
                            return (
                                <FadeIn key={chapter.number} delay={i * 0.12} className={featured ? "lg:row-span-2" : ""}>
                                    <article
                                        data-testid={`manifesto-chapter-${chapter.number}`}
                                        className={`group relative h-full overflow-hidden rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1.5 sm:p-10 ${
                                            featured
                                                ? "grain border-white/10 bg-[#08104F] hover:shadow-[0_30px_70px_rgba(8,16,79,0.35)]"
                                                : "border-[#08104F]/8 bg-[#FFFDFA] hover:border-[#0047BB]/25 hover:shadow-[0_24px_60px_rgba(8,16,79,0.12)]"
                                        }`}
                                    >
                                        {featured && (
                                            <span
                                                aria-hidden="true"
                                                className="gota pointer-events-none absolute -right-16 -top-16 h-56 w-56 opacity-25"
                                                style={{
                                                    background:
                                                        "linear-gradient(135deg, #FCB21E 0%, #F35E6A 50%, #5E11BD 100%)",
                                                }}
                                            />
                                        )}
                                        <div className="relative flex items-start justify-between">
                                            <span
                                                className={`font-display transition-colors duration-300 ${
                                                    featured
                                                        ? "text-7xl text-white/10 group-hover:text-white/20"
                                                        : "text-5xl text-[#08104F]/10 group-hover:text-[#0047BB]/25"
                                                }`}
                                            >
                                                {chapter.number}
                                            </span>
                                            <chapter.icon
                                                className={`h-6 w-6 ${featured ? "text-[#B2D0FF]" : "text-[#0047BB]"}`}
                                                strokeWidth={1.75}
                                            />
                                        </div>
                                        <p
                                            className={`relative mt-8 text-[11px] font-semibold uppercase tracking-[0.2em] ${
                                                featured ? "text-[#FCB21E]" : "text-[#0047BB]"
                                            }`}
                                        >
                                            {chapter.tag}
                                        </p>
                                        <h3
                                            className={`relative mt-3 font-display leading-snug ${
                                                featured
                                                    ? "text-2xl text-[#F3EDE6] sm:text-3xl"
                                                    : "text-xl text-[#08104F] sm:text-2xl"
                                            }`}
                                        >
                                            {chapter.title}
                                        </h3>
                                        <p
                                            className={`relative mt-4 leading-relaxed ${
                                                featured ? "text-base text-[#B2D0FF]" : "text-sm text-[#3A4768]"
                                            }`}
                                        >
                                            {chapter.text}
                                        </p>
                                    </article>
                                </FadeIn>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Simulator />

            <section
                data-testid="section-ilove-ecosystem"
                className="grain relative overflow-hidden bg-[#FFF3E6] px-5 py-20 sm:px-8 sm:py-28"
            >
                <div
                    className="pointer-events-none absolute right-0 top-0 h-[400px] w-[600px] rounded-full opacity-70"
                    style={{ background: "radial-gradient(ellipse at top right, rgba(255,130,0,0.22), transparent 65%)" }}
                />
                <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[0.85fr_1.15fr]">
                    <FadeIn>
                        <p className="inline-flex items-center gap-2 rounded-full border border-[#FF8200]/30 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#A34A00]">
                            <Sparkle className="h-3 w-3" />
                            Camada de tecnologia do ecossistema
                        </p>
                        <h2 className="mt-6 font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl lg:text-5xl">
                            ilove.me + Contabeleza: uma rotina integrada de gestão
                        </h2>
                        <p className="mt-5 max-w-lg text-base leading-relaxed text-[#2A3160] sm:text-lg">
                            A Contabeleza atua em um ecossistema integrado ao ilove.me, plataforma voltada à
                            gestão e aos agendamentos dos negócios de beleza. Essa conexão contribui para uma
                            rotina mais organizada e para uma visão integrada da operação.
                        </p>
                        <ul className="mt-9 space-y-4">
                            {ILOVE_POINTS.map((point) => (
                                <li key={point.text} className="flex items-center gap-4">
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[#FF8200]/25 bg-white shadow-[0_6px_16px_rgba(255,130,0,0.15)]">
                                        <point.icon className="h-6 w-6 text-[#C25E00]" strokeWidth={1.75} />
                                    </span>
                                    <span className="text-base font-semibold text-[#08104F] sm:text-lg">{point.text}</span>
                                </li>
                            ))}
                        </ul>
                        <a
                            href={ILOVEME_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-ilove-learn-more"
                            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#08104F] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0047BB] active:scale-[0.97]"
                        >
                            Explorar o ecossistema
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </a>
                    </FadeIn>

                    <FadeIn delay={0.15}>
                        <div className="relative lg:-mr-10 xl:-mr-20">
                            <div
                                className="pointer-events-none absolute -inset-6 rounded-[2.5rem] opacity-80 blur-2xl"
                                style={{ background: "linear-gradient(135deg, rgba(255,130,0,0.35), rgba(243,94,106,0.2))" }}
                            />
                            <div className="relative">
                                <img
                                    src="/images/ilove-me-sistema.png"
                                    alt="Plataforma ilove.me no computador e no celular, com agenda da equipe e agendamento online"
                                    width="1768"
                                    height="854"
                                    loading="lazy"
                                    className="h-auto w-full"
                                    style={{
                                        maskImage: "linear-gradient(to bottom, #000 60%, transparent)",
                                        WebkitMaskImage: "linear-gradient(to bottom, #000 60%, transparent)",
                                    }}
                                />
                            </div>
                            <Sparkle className="absolute -left-4 -top-4 h-10 w-10" />
                        </div>
                    </FadeIn>
                </div>
            </section>

            <section className="relative overflow-hidden bg-[#E6EEFF] px-5 py-20 sm:px-8 sm:py-28">
                <div
                    className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[620px] rounded-full"
                    style={{ background: "radial-gradient(ellipse at top left, rgba(0,71,187,0.12), transparent 65%)" }}
                />
                <div className="relative mx-auto max-w-7xl">
                    <FadeIn className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <Eyebrow>Para quem faz a beleza acontecer</Eyebrow>
                            <h2 className="mt-4 max-w-xl font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl">
                                Cada negócio da beleza tem a sua rotina. A gente fala a língua de todos.
                            </h2>
                        </div>
                        <Link
                            to="/para-quem"
                            className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-[#0047BB]"
                        >
                            Ver todas as trilhas
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                    </FadeIn>

                    <div data-testid="audience-tabs-container" className="mt-12 grid gap-6 md:grid-cols-3">
                        {PROFILES.map((profile, i) => (
                            <FadeIn
                                key={profile.title}
                                delay={i * 0.1}
                                className={["lg:-rotate-1", "lg:mt-12", "lg:rotate-1"][i]}
                            >
                                <Link
                                    to={profile.to}
                                    data-testid={profile.testId}
                                    className="group block overflow-hidden rounded-3xl border border-[#0047BB]/10 bg-white shadow-[0_10px_30px_rgba(8,16,79,0.06)] transition-all duration-300 hover:-translate-y-1.5 hover:rotate-0 hover:shadow-[0_24px_60px_rgba(8,16,79,0.12)]"
                                >
                                    <div
                                        className={`aspect-[4/3] overflow-hidden ${
                                            ["gota-frame", "", "gota-frame"][i]
                                        }`}
                                    >
                                        <img
                                            src={profile.img}
                                            alt={profile.title}
                                            width="800"
                                            height="600"
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="p-7">
                                        <h3 className="font-display text-xl text-[#08104F]">{profile.title}</h3>
                                        <p className="mt-2.5 text-sm leading-relaxed text-[#3A4768]">{profile.text}</p>
                                        <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#0047BB]">
                                            Conhecer a trilha
                                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                                        </span>
                                    </div>
                                </Link>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="section-testimonials-carousel" className="bg-[#FFFDFA] px-5 py-20 sm:px-8 sm:py-28">
                <div className="mx-auto max-w-7xl">
                    <FadeIn>
                        <Eyebrow>Prova social</Eyebrow>
                        <h2 className="mt-4 max-w-2xl font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl lg:text-5xl">
                            “Não falamos sobre nós. <span className="text-gradient-tricolor">Eles falam.</span>”
                        </h2>
                    </FadeIn>

                    <div className="mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {TESTIMONIALS.map((t, i) => (
                            <FadeIn
                                key={t.name}
                                delay={Math.min(i * 0.06, 0.3)}
                                className="w-[85vw] shrink-0 snap-center sm:w-[26rem]"
                            >
                                <figure className="flex h-full flex-col justify-between rounded-3xl border border-[#08104F]/8 bg-[#F3EDE6] p-8">
                                    <div>
                                        <Quote className="h-6 w-6 text-[#0047BB]/40" fill="currentColor" strokeWidth={0} />
                                        <blockquote className="mt-5 font-display text-lg leading-relaxed text-[#08104F]">
                                            {t.quote}
                                        </blockquote>
                                    </div>
                                    <figcaption className="mt-8 flex items-center gap-3.5">
                                        {t.img ? (
                                            <img
                                                src={t.img}
                                                alt={t.name}
                                                loading="lazy"
                                                className="h-11 w-11 rounded-full border border-[#08104F]/10 object-cover"
                                            />
                                        ) : (
                                            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#08104F] font-display text-sm text-[#F3EDE6]">
                                                {t.name.charAt(0)}
                                            </span>
                                        )}
                                        <span>
                                            <span className="block text-sm font-semibold text-[#08104F]">{t.name}</span>
                                            <span className="block text-xs text-[#3A4768]/75">{t.business}</span>
                                        </span>
                                    </figcaption>
                                </figure>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <section
                aria-labelledby="resumo-contabeleza"
                className="bg-[#FFFDFA] px-5 py-20 sm:px-8 sm:py-28"
            >
                <div className="mx-auto max-w-7xl">
                    <FadeIn>
                        <Eyebrow>A Contabeleza, em resumo</Eyebrow>
                        <h2
                            id="resumo-contabeleza"
                            className="mt-4 max-w-2xl font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl"
                        >
                            Quem somos, o que fazemos e para quem
                        </h2>
                    </FadeIn>
                    <dl className="mt-12 grid gap-x-10 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
                        {[
                            [
                                "Quem é a Contabeleza",
                                "Uma contabilidade especializada no mercado da beleza, com equipe dedicada a salões, barbearias, clínicas de estética, esmalterias, spas e profissionais parceiros.",
                            ],
                            [
                                "O que fazemos",
                                "Contabilidade completa, gestão tributária, BPO financeiro, abertura e regularização de empresas, rotinas de equipe e apoio na implantação da plataforma ilove.me.",
                            ],
                            [
                                "Para quem",
                                "Donos e gestores de negócios da beleza, do salão de bairro às grandes redes, e profissionais que atuam como parceiros.",
                            ],
                            [
                                "Quais problemas resolvemos",
                                "Insegurança na relação com profissionais, tributos desalinhados com a operação, financeiro desorganizado e informalidade que trava o crescimento.",
                            ],
                            [
                                "Segmentos atendidos",
                                "Salões de beleza, barbearias, esmalterias, spas, clínicas de estética e profissionais autônomos do setor.",
                            ],
                            [
                                "Como funciona o atendimento",
                                "Diagnóstico gratuito, plano de ação claro e rotina organizada mês a mês, com atendimento direto pelo WhatsApp e linguagem sem contabilês.",
                            ],
                        ].map(([term, def], i) => (
                            <FadeIn key={term} delay={Math.min(i * 0.06, 0.25)}>
                                <div className="border-l-2 border-[#0047BB]/25 pl-5">
                                    <dt className="font-display text-base text-[#08104F]">{term}</dt>
                                    <dd className="mt-2 text-sm leading-relaxed text-[#3A4768]">{def}</dd>
                                </div>
                            </FadeIn>
                        ))}
                    </dl>
                    <FadeIn delay={0.2} className="mt-10 flex flex-wrap gap-3">
                        {[
                            { label: "Salões de beleza", to: "/salao-de-beleza" },
                            { label: "Barbearias", to: "/barbearia" },
                            { label: "Esmalterias", to: "/esmalteria" },
                            { label: "Spas", to: "/spa" },
                        ].map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className="rounded-full border border-[#08104F]/12 bg-[#F3EDE6] px-4 py-2 text-xs font-medium text-[#08104F] transition-colors duration-200 hover:border-[#0047BB] hover:text-[#0047BB]"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </FadeIn>
                </div>
            </section>

            <SwitchAccountant />

            <CtaBand
                title="Seu negócio não tem tempo a perder."
                text="Diagnóstico gratuito com um especialista que entende de beleza, e domina a Lei do Salão Parceiro."
            />
        </>
    );
}
